import { Component, TemplateRef, ViewChild, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EnvironmentService, SgConnectionStateService, SgPoller, UserSessionService } from '@sg/common';
import { SgInputType, SgToastService } from '@sg/ui-kit/core';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { FormControl } from '@angular/forms';

declare module '@pebula/nform/lib/types/form-element-type' {
  interface FormElementType {
    'sg-input': {
      type: SgInputType;
      variant: 'primary' | 'secondary';
      size: 'small' | 'default';
      width: string;
      label: string;
      placeholder: string;
      hideLabel: boolean;
      errorsMap: { [error: 'minlength' | 'maxlength' | 'pattern' | string]: string | RegExp };
      hint: string;
      maxLimit: number;
      prefix: string;
      prefixIcon: string;
      suffix: string;
      showSuffixAction: boolean;
      clearButton: boolean;
      numberStep: string;
      min: number;
      max: number;
      submitButtonIcon: string;
      submitButtonIsLoading: boolean;
      subscriptSizing: 'fixed' | 'dynamic';
      errorFallbackMessage: string;
      valueSubmitted: string | number;
    };
    'sg-toggle': {
      label: string;
      size: 'default' | 'small';
    };
    'sg-textarea': {
      variant: 'primary' | 'secondary';
      width: string;
      placeholder: string;
      hideLabel: boolean;
      errorsMap: { [error: 'minlength' | 'maxlength' | 'pattern' | string]: string | RegExp };
      hint: string;
      maxLimit: number;
      rows: number;
      minRows: number;
      maxRows: number;
      autoResize: boolean;
      subscriptSizing: 'fixed' | 'dynamic';
      errorFallbackMessage: string;
    };
  }
}

@Component({
  selector: 'sg-basic-app-init-and-templates',
  templateUrl: './basic-app-init-and-templates.component.html',
  styleUrls: ['./basic-app-init-and-templates.component.scss'],
  standalone: false,
})
export class SgBasicAppInitAndTemplatesComponent {
  @ViewChild('offlineDialog', { read: TemplateRef, static: true }) offlineDialogTemplate: TemplateRef<any>;
  fc = new FormControl<string | number>(null);
  constructor(
    @Inject(Idle) idle: Idle,
    connectionState: SgConnectionStateService,
    private toastService: SgToastService,
    private userSession: UserSessionService<any>,
    private envService: EnvironmentService,
    private poller: SgPoller,
    private dialog: MatDialog
  ) {
    // handling idle state.
    this.initIdleDetection(idle);
    // handling network connection state.
    this.initConnectionDetection(connectionState);
  }

  private initIdleDetection(idle: Idle): void {
    let warn: any;

    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    idle.setIdle(this.envService.idle[0]); // idle after 60 secs
    idle.setTimeout(this.envService.idle[1]); // logout after 360 secs
    idle.onIdleStart.subscribe(() => this.poller.stopAll());
    idle.onIdleEnd.subscribe(() => {
      this.poller.startAll();
      if (warn) {
        warn.dismiss();
        warn = undefined;
      }
    });
    if (this.envService.env !== 'test') {
      idle.onTimeout.subscribe(() => this.userSession.logout());
    }
    idle.onTimeoutWarning.subscribe((countdown) => {
      if (countdown < 31) {
        const msg = { message: `Log out due to inactivity in ${countdown} seconds` };
        if (!warn) {
          warn = this.toastService.warning({ title: msg.message, showCloseButton: false });
        } else {
          warn.convertTo('warning', { title: msg.message });
        }
      }
    });
    idle.watch();
  }

  private initConnectionDetection(connectionState: SgConnectionStateService): void {
    // TODO: handle when we are offline at startup.
    // while this should never occur because we need to login before, it might be an edge case so we need to handle it.
    let dialogRef: MatDialogRef<any>;
    const closeDialogRef = () => {
      if (dialogRef) {
        dialogRef.close();
        dialogRef = undefined;
      }
    };

    connectionState.stateChange.subscribe((onLine) => {
      if (onLine) {
        closeDialogRef();
        this.poller.startAll();
      } else {
        closeDialogRef();
        dialogRef = this.dialog.open(this.offlineDialogTemplate, {
          disableClose: true,
          closeOnNavigation: false,
        });
        this.poller.stopAll();
      }
    });
  }
}
